// packages/web/src/components/Header.tsx
import React, { useState } from 'react'
import {
  ShoppingBag,
  Search,
  PackageSearch,
  Menu,
  X,
  Sparkles,
  PhoneCall,
  Award,
  LogOut,
  Sun,
  Moon,
  ShieldCheck,
} from 'lucide-react'
import { useCart } from '../context/CartContext'
import { useAuth } from '../context/AuthContext'
import { useSiteConfig } from '../context/SiteConfigContext'
import { useTheme } from '../context/ThemeContext'
import { useCategoriesWithProducts } from '../lib/storeCategories'
import { useNavigate } from '@tanstack/react-router'
import { assetSrc } from '../lib/assets'

interface HeaderProps {
  onSearch: (query: string) => void
  onOpenTracking: () => void
  onOpenHallmarkGuide: () => void
  selectedCategory?: string | undefined
  setSelectedCategory: (cat: string | undefined) => void
}

export const Header: React.FC<HeaderProps> = ({
  onSearch,
  onOpenTracking,
  onOpenHallmarkGuide,
  selectedCategory,
  setSelectedCategory,
}) => {
  const { totalItemsCount, setIsCartOpen } = useCart()
  const { isAuthenticated, isAdmin, isCustomer, logout, user } = useAuth()
  const { config } = useSiteConfig()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const { categories: navCategories, loading: navCategoriesLoading } = useCategoriesWithProducts()
  const [searchInput, setSearchInput] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSearch(searchInput)
    const catalogEl = document.getElementById('catalog')
    if (catalogEl) {
      catalogEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const handleLogout = () => {
    const goAdminLogin = isAdmin
    logout()
    setMobileMenuOpen(false)
    navigate({ to: goAdminLogin ? '/login' : '/', replace: true })
  }

  return (
    <header className="sticky top-0 z-50 bg-[var(--bg-main)]/95 backdrop-blur-xl border-b border-[var(--border-gold)] shadow-2xl">
      {/* Top Announcement Bar */}
      <div className="bg-gradient-to-r from-[var(--bg-main)] via-[var(--bg-surface-2)] to-[var(--bg-main)] border-b border-[var(--border-gold)]/15 py-1.5 px-3 sm:px-8 lg:px-12 text-[10px] sm:text-[11px] font-sans text-[var(--gold-light)]">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between gap-2">
          {/* Mobile Announcement Marquee Ticker */}
          <div className="flex sm:hidden w-full items-center justify-between overflow-hidden text-[10px] font-semibold text-[var(--gold-main)] relative">
            <div className="overflow-hidden w-full relative h-4">
              <span className="animate-marquee absolute whitespace-nowrap flex items-center gap-2">
                <Award className="w-3 h-3 text-[var(--gold-main)] inline shrink-0" />
                <span>{config.announcement_ticker}</span>
                <span className="text-[var(--text-faint)]">•</span>
                <span>1-Year Color Replacement Guarantee</span>
                <span className="text-[var(--text-faint)]">•</span>
                <span>COD Shipping Across India</span>
              </span>
            </div>
            <button
              onClick={onOpenHallmarkGuide}
              className="text-[var(--text-muted)] underline underline-offset-2 flex items-center gap-1 text-[9px] shrink-0 ml-2 bg-[var(--bg-main)] px-1 z-10"
            >
              <Sparkles className="w-2.5 h-2.5 text-[var(--gold-main)]" /> Info
            </button>
          </div>

          {/* Desktop Announcement */}
          <div className="hidden sm:flex items-center gap-2 font-medium">
            <span className="inline-flex items-center gap-1 bg-[var(--gold-main)]/20 px-2 py-0.5 rounded-full text-[10px] font-bold text-[var(--gold-main)] border border-[var(--gold-main)]/30">
              <Award className="w-3 h-3" /> OFFICIAL STORE
            </span>
            <span className="text-[var(--text-muted)]">
              {config.announcement_ticker}
            </span>
          </div>

          <div className="hidden sm:flex items-center gap-4 text-[var(--text-muted)]">
            <button
              onClick={onOpenHallmarkGuide}
              className="hover:text-[var(--gold-main)] transition underline underline-offset-2 flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3 text-[var(--gold-main)]" />
              Quality & Warranty Policy
            </button>
            <span className="hidden md:inline text-[var(--text-faint)]">|</span>
            <a
              href={`tel:${config.contact_phone.replace(/[^\d+]/g, '')}`}
              className="flex items-center gap-1 hover:text-[var(--gold-main)]"
            >
              <PhoneCall className="w-3 h-3 text-[var(--gold-main)]" />
              Contact Us
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Header */}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Brand Logo */}
          <div
            className="flex items-center gap-2.5 cursor-pointer group"
            onClick={() => {
              navigate({ to: '/' })
              setSelectedCategory(undefined)
            }}
          >
            {config.logo_url ? (
              <img
                src={assetSrc(config.logo_url)}
                alt={config.brand_name}
                className="h-10 sm:h-12 w-auto object-contain"
              />
            ) : (
              <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-gradient-to-tr from-[var(--gold-deep)] via-[var(--gold-main)] to-[var(--gold-light)] p-0.5 shadow-lg group-hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition">
                <div className="w-full h-full rounded-full bg-[var(--bg-main)] flex items-center justify-center">
                  <span className="font-serif font-black text-lg sm:text-xl gold-shimmer-text">
                    {config.brand_name ? config.brand_name.charAt(0) : 'B'}
                  </span>
                </div>
              </div>
            )}
            <div>
              <span className="text-base sm:text-2xl font-bold tracking-wider font-title gold-shimmer-text uppercase">
                {config.brand_name}
              </span>
              <p className="hidden sm:block text-[9px] text-[var(--gold-main)]/70 uppercase tracking-[0.25em] font-sans -mt-0.5">
                {config.brand_tagline}
              </p>
            </div>
          </div>

          {/* Search Input Bar (Desktop) */}
          <form onSubmit={handleSearchSubmit} className="hidden lg:flex flex-1 max-w-md mx-8 relative">
            <div className="w-full relative border-gold-glow rounded-full">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  const val = e.target.value
                  setSearchInput(val)
                  onSearch(val)
                  if (val.trim().length > 0) {
                    const catalogEl = document.getElementById('catalog')
                    if (catalogEl) {
                      catalogEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  }
                }}
                placeholder="Search rings, haram, bracelets, jhumkas..."
                className="w-full bg-[var(--bg-surface)]/80 border-0 rounded-full py-2.5 pl-10 pr-12 text-xs text-[var(--text-primary)] placeholder-[var(--text-faint)] focus:ring-0"
              />
              <Search className="absolute left-3.5 top-3 w-4 h-4 text-[var(--gold-main)]" />
              <span className="absolute right-3 top-2.5 text-[10px] font-mono text-[var(--text-faint)] bg-[var(--bg-surface-2)] border border-[var(--border)] px-1.5 py-0.5 rounded">
                ⌘K
              </span>
            </div>
          </form>

          {/* Header Controls (Desktop) */}
          <div className="hidden sm:flex items-center gap-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              className="p-2 text-[var(--text-primary)] hover:text-[var(--gold-main)] transition rounded-lg hover:bg-[var(--bg-surface-2)]"
            >
              {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </button>

            {isAdmin && (
              <button
                onClick={() => navigate({ to: '/admin' })}
                className="btn-gold-outline text-xs py-2 px-3.5 flex items-center gap-1.5"
                title="Open Admin Dashboard"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-[var(--gold-main)]" />
                <span>Admin Portal</span>
              </button>
            )}

            {isCustomer && (
              <button
                onClick={() => navigate({ to: '/account/orders' })}
                className="text-xs font-medium text-[var(--text-primary)] hover:text-[var(--gold-main)] transition flex items-center gap-1.5 px-3 py-2 rounded-xl hover:bg-[var(--bg-surface-2)] border border-transparent hover:border-[var(--border-gold)]"
              >
                <PackageSearch className="w-4 h-4 text-[var(--gold-main)]" />
                <span>My Orders</span>
              </button>
            )}

            {!isAuthenticated && (
              <button
                onClick={() => navigate({ to: '/account/login' })}
                className="text-xs font-medium text-[var(--text-primary)] hover:text-[var(--gold-main)] transition px-3 py-2 rounded-xl hover:bg-[var(--bg-surface-2)]"
              >
                Sign In
              </button>
            )}

            <button
              onClick={onOpenTracking}
              className="text-xs font-medium text-[var(--text-primary)] hover:text-[var(--gold-main)] transition flex items-center gap-1.5 px-3 py-2 rounded-xl hover:bg-[var(--bg-surface-2)] border border-transparent hover:border-[var(--border-gold)]"
            >
              <PackageSearch className="w-4 h-4 text-[var(--gold-main)]" />
              <span>Track Order</span>
            </button>

            <button
              onClick={() => setIsCartOpen(true)}
              className="btn-gold text-xs py-2 px-4 shadow-lg flex items-center gap-2 group"
            >
              <ShoppingBag className="w-4 h-4 group-hover:scale-110 transition" />
              <span>Bag</span>
              {totalItemsCount > 0 && (
                <span className="bg-[var(--bg-main)] text-[var(--gold-main)] text-[11px] font-extrabold w-5 h-5 rounded-full flex items-center justify-center ml-1 border border-[var(--gold-main)]">
                  {totalItemsCount}
                </span>
              )}
            </button>

            {isAuthenticated && (
              <button
                onClick={handleLogout}
                title={user?.name ? `Logout (${user.name})` : 'Logout'}
                className="p-2 text-[var(--text-faint)] hover:text-red-400 transition hover:bg-[var(--bg-surface-2)] rounded-lg"
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Mobile Right Quick Action Controls */}
          <div className="flex sm:hidden items-center gap-2">
            {/* Mobile Theme Toggle */}
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              className="p-2 text-[var(--text-primary)] hover:text-[var(--gold-main)] transition rounded-lg"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>

            {/* Mobile Search Toggle */}
            <button
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              className="p-2 text-[var(--text-primary)] hover:text-[var(--gold-main)] transition rounded-lg"
              title="Search"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Mobile Cart Bag Button with Badge Counter */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="p-2 text-[var(--gold-main)] relative transition rounded-lg hover:bg-[var(--bg-surface)]"
              title="View Bag"
            >
              <ShoppingBag className="w-5 h-5" />
              {totalItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[var(--gold-main)] text-[var(--bg-main)] text-[10px] font-black w-4 h-4 rounded-full flex items-center justify-center shadow-md">
                  {totalItemsCount}
                </span>
              )}
            </button>

            {/* Mobile Hamburger Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-[var(--text-primary)] hover:text-[var(--gold-main)] transition rounded-lg"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Search Drawer Input Bar */}
        {mobileSearchOpen && (
          <div className="sm:hidden pb-3 animate-fadeIn">
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  const val = e.target.value
                  setSearchInput(val)
                  onSearch(val)
                  if (val.trim().length > 0) {
                    const catalogEl = document.getElementById('catalog')
                    if (catalogEl) {
                      catalogEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  }
                }}
                placeholder="Search gold plated jewellery, rings, haram..."
                className="w-full bg-[var(--bg-surface)] border border-[var(--gold-main)]/30 rounded-xl py-2 pl-9 pr-4 text-xs text-[var(--text-primary)] placeholder-[var(--text-faint)] focus:outline-none focus:border-[var(--gold-main)]"
                autoFocus
              />
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-[var(--gold-main)]" />
            </form>
          </div>
        )}

        {/* Category Navigation Pills — only categories with products */}
        {!navCategoriesLoading && navCategories.length > 0 && (
        <nav className="hidden sm:flex items-center gap-6 py-2.5 border-t border-[var(--border)] text-xs">
          {[
            { label: 'All Catalogue', value: undefined },
            ...navCategories.map((c) => ({ label: c.navLabel || c.label, value: c.slug })),
          ].map((cat) => {
            const isSelected =
              cat.value === undefined
                ? selectedCategory === undefined
                : selectedCategory?.toUpperCase() === cat.value.toUpperCase()
            return (
              <button
                key={cat.label}
                onClick={() => setSelectedCategory(cat.value)}
                className={`pb-1 transition font-medium relative ${
                  isSelected
                    ? 'text-[var(--gold-main)] font-bold'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                }`}
              >
                {cat.label}
                {isSelected && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--gold-main)] rounded-full" />
                )}
              </button>
            )
          })}
        </nav>
        )}
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="sm:hidden bg-[var(--bg-surface)] border-t border-[var(--border-gold)] px-4 py-4 space-y-4 animate-fadeIn">
          {!navCategoriesLoading && navCategories.length > 0 && (
          <div>
            <p className="text-[10px] text-[var(--gold-main)] font-bold uppercase tracking-wider mb-2">Categories</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'All Collections', value: undefined },
                ...navCategories.map((c) => ({ label: c.navLabel || c.label, value: c.slug })),
              ].map((cat) => (
                <button
                  key={cat.label}
                  onClick={() => {
                    setSelectedCategory(cat.value)
                    setMobileMenuOpen(false)
                  }}
                  className={`text-left p-2 rounded-lg text-xs transition ${
                    cat.value === undefined
                      ? selectedCategory === undefined
                      : selectedCategory?.toUpperCase() === cat.value.toUpperCase()
                      ? 'bg-[var(--gold-main)]/20 text-[var(--gold-main)] font-bold border border-[var(--gold-main)]/40'
                      : 'bg-[var(--bg-surface-2)] text-[var(--text-primary)] hover:bg-[var(--bg-surface-3)]'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
          )}

          <div className="pt-3 border-t border-[var(--border)] space-y-2">
            {isAuthenticated && (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full text-left py-2 px-3 rounded-lg bg-[var(--bg-surface-2)] text-xs text-red-400"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            )}

            {!isAuthenticated && (
              <button
                onClick={() => {
                  navigate({ to: '/account/login' })
                  setMobileMenuOpen(false)
                }}
                className="flex items-center gap-2 w-full text-left py-2 px-3 rounded-lg bg-[var(--bg-surface-2)] text-xs text-[var(--text-primary)]"
              >
                Sign In
              </button>
            )}

            {isCustomer && (
              <button
                onClick={() => {
                  navigate({ to: '/account/orders' })
                  setMobileMenuOpen(false)
                }}
                className="flex items-center gap-2 w-full text-left py-2 px-3 rounded-lg bg-[var(--bg-surface-2)] text-xs text-[var(--text-primary)]"
              >
                <PackageSearch className="w-4 h-4 text-[var(--gold-main)]" />
                <span>My Orders</span>
              </button>
            )}

            <button
              onClick={() => {
                onOpenTracking()
                setMobileMenuOpen(false)
              }}
              className="flex items-center gap-2 w-full text-left py-2 px-3 rounded-lg bg-[var(--bg-surface-2)] text-xs text-[var(--text-primary)]"
            >
              <PackageSearch className="w-4 h-4 text-[var(--gold-main)]" />
              <span>Track Your Order</span>
            </button>

            <a
              href={`tel:${config.contact_phone.replace(/[^\d+]/g, '')}`}
              className="flex items-center gap-2 w-full text-left py-2 px-3 rounded-lg bg-[var(--bg-surface-2)] text-xs text-[var(--text-primary)]"
            >
              <PhoneCall className="w-4 h-4 text-[var(--gold-main)]" />
              <span>Contact Us</span>
            </a>

            {isAdmin && (
              <button
                onClick={() => {
                  navigate({ to: '/admin' })
                  setMobileMenuOpen(false)
                }}
                className="flex items-center gap-2 w-full text-left py-2 px-3 rounded-lg bg-[var(--gold-main)]/20 text-xs text-[var(--gold-main)] font-bold"
              >
                <ShieldCheck className="w-4 h-4 text-[var(--gold-main)]" />
                <span>Admin Portal</span>
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  )
}
